import React, { useState } from "react";
import { X, Clock, Zap, Star, Crown, CreditCard, Check, Sparkles, Loader2 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLANS = [
  {
    id: "monthly",
    name: "Monthly",
    description: "Billed monthly",
    price: "9.99",
    period: "month",
    icon: Zap,
    color: "text-blue-600",
    border: "border-blue-200 hover:border-blue-400",
    btnClass: "bg-blue-600 hover:bg-blue-500 text-white",
    features: ["Unlimited shift tracking", "Full CPT code database", "Calendar & history views", "CSV export"],
  },
  {
    id: "yearly",
    name: "Yearly",
    description: "Save with an Annual Plan",
    price: "99",
    period: "year",
    icon: Star,
    color: "text-green-600",
    border: "border-green-200 hover:border-green-400",
    btnClass: "bg-green-600 hover:bg-green-500 text-white",
    features: ["Unlimited shift tracking", "Full CPT code database", "Calendar & history views", "CSV export"],
  },
  {
    id: "lifetime",
    name: "Lifetime",
    description: "One-time payment, forever access",
    price: "199",
    period: "lifetime",
    icon: Crown,
    color: "text-amber-600",
    border: "border-amber-300 hover:border-amber-500",
    btnClass: "bg-amber-500 hover:bg-amber-400 text-black font-semibold",
    bestValue: true,
    features: ["Unlimited shift tracking", "Full CPT code database", "Calendar & history views", "CSV export", "Forever access - pay once!"],
  },
];

export default function PlanSelectionModal({ onClose }) {
  const [loading, setLoading] = useState(null);
  const [error, setError] = useState("");

  const handleSelectPlan = async (planId) => {
    setLoading(planId);
    setError("");
    try {
      const res = await axios.post(`${API}/subscription/guest-checkout`, {
        plan_id: planId,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to start checkout. Please try again.");
      setLoading(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)" }}
    >
      <div
        className="relative w-full max-w-5xl rounded-2xl bg-gray-50 shadow-2xl overflow-hidden"
        style={{ maxHeight: "90vh", overflowY: "auto" }}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          data-testid="plan-modal-close"
          className="absolute top-4 right-4 z-10 p-1.5 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
        >
          <X size={20} />
        </button>

        <div className="py-10 px-6 md:px-10">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 bg-gradient-to-br from-blue-100 to-purple-100">
              <Sparkles className="h-8 w-8 text-blue-600" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Choose Your Plan</h2>
            <p className="text-lg text-gray-600">Unlock full access to Doc RVU Tracker</p>
          </div>

          {error && (
            <div className="mb-4 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
              {error}
            </div>
          )}

          {/* Free Trial Banner */}
          <div className="mb-8 rounded-xl bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 px-5 py-5">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-full bg-blue-100 flex-shrink-0">
                  <Clock className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg text-gray-900">Start Your 7-Day Free Trial</h3>
                  <p className="text-sm text-gray-600">Try all features free. Auto-enrolls to monthly ($9.99/mo) after trial.</p>
                  <p className="text-xs mt-0.5 text-gray-400">Cancel anytime before trial ends to avoid charges.</p>
                </div>
              </div>
              <button
                data-testid="plan-select-trial"
                onClick={() => handleSelectPlan("trial")}
                disabled={!!loading}
                className="flex-shrink-0 flex items-center justify-center gap-2 px-8 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-500 text-white font-semibold text-sm transition-all disabled:opacity-50 min-w-[160px]"
              >
                {loading === "trial" ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  "Start Free Trial"
                )}
              </button>
            </div>
          </div>

          {/* Divider */}
          <div className="flex items-center gap-4 mb-7">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-sm text-gray-400">Or choose a plan</span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>

          {/* Plans Grid */}
          <div className="grid md:grid-cols-3 gap-5">
            {PLANS.map((plan) => {
              const Icon = plan.icon;
              const isLoading = loading === plan.id;
              return (
                <div
                  key={plan.id}
                  data-testid={`plan-card-${plan.id}`}
                  className={`relative bg-white rounded-xl border-2 transition-all duration-200 flex flex-col ${plan.border} ${
                    plan.bestValue ? "md:scale-105 shadow-xl" : "shadow-sm"
                  }`}
                >
                  {plan.bestValue && (
                    <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-bold bg-amber-400 text-amber-900 shadow-sm whitespace-nowrap">
                      BEST VALUE
                    </div>
                  )}

                  <div className="p-6 text-center flex flex-col flex-1">
                    {/* Icon */}
                    <div className={`mx-auto w-12 h-12 rounded-full flex items-center justify-center mb-3 bg-gray-100 ${plan.color}`}>
                      <Icon className="h-6 w-6" />
                    </div>

                    {/* Name & description */}
                    <h3 className="text-xl font-bold text-gray-900">{plan.name}</h3>
                    <p className="text-sm text-gray-500 mb-4">{plan.description}</p>

                    {/* Price */}
                    <div className="mb-5">
                      <span className="text-4xl font-bold text-gray-900">${plan.price}</span>
                      {plan.period !== "lifetime" && (
                        <span className="text-lg text-gray-500">/{plan.period}</span>
                      )}
                    </div>

                    {/* Features */}
                    <ul className="space-y-2 text-left text-sm text-gray-600 mb-6 flex-1">
                      {plan.features.map((f) => (
                        <li key={f} className="flex items-center gap-2">
                          <Check className={`h-4 w-4 flex-shrink-0 ${plan.color}`} />
                          <span className={f.includes("pay once") ? "font-semibold text-gray-800" : ""}>{f}</span>
                        </li>
                      ))}
                    </ul>

                    {/* CTA Button */}
                    <button
                      data-testid={`plan-select-${plan.id}`}
                      onClick={() => handleSelectPlan(plan.id)}
                      disabled={!!loading}
                      className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-all disabled:opacity-50 ${plan.btnClass}`}
                    >
                      {isLoading ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <>
                          <CreditCard size={15} />
                          {plan.id === "lifetime" ? "Get Lifetime Access" : "Subscribe"}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <p className="text-center text-sm text-gray-400 mt-8">
            Secure payment powered by Stripe. Cancel subscriptions anytime from your account.
          </p>
        </div>
      </div>
    </div>
  );
}
